// frontend/src/pages/admin/AdminTicketDetail.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supportApi } from '../../api/support';
import uploadService from '../../services/upload';
import { compressImage } from '../../utils/imageCompression';
import { 
    FaArrowLeft, FaPaperPlane, FaPaperclip, FaSpinner, FaUser, FaUserShield, 
    FaLock, FaBox, FaTools, FaCheckCircle, FaExclamationCircle // <--- ADDED FaExclamationCircle
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';

function AdminTicketDetail() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Reply State
  const [replyText, setReplyText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const messagesEndRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    try {
      const data = await supportApi.getTicketById(ticketId);
      setTicket(data);
    } catch (err) {
      console.error("Failed to load ticket", err);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages]);

  const handleStatusChange = async (newStatus) => {
      if(!window.confirm(`Change status to ${newStatus.toUpperCase()}?`)) return;
      try {
          await supportApi.updateTicketStatusAdmin(ticketId, newStatus);
          setTicket(prev => ({ ...prev, status: newStatus }));
      } catch (err) {
          alert("Failed to update status.");
      }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const compressed = await compressImage(file, 0.6);
        const res = await uploadService.uploadImage(compressed, 'support-attachments');
        return res.url;
      });
      const urls = await Promise.all(uploadPromises);
      setAttachments(prev => [...prev, ...urls]);
    } catch (err) {
      alert("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      await supportApi.replyToTicket(ticketId, {
        message: replyText,
        attachments: attachments,
        is_internal_note: isInternalNote
      });
      setReplyText('');
      setAttachments([]);
      setIsInternalNote(false); // Reset to public reply
      fetchTicket(); 
    } catch (err) {
      alert("Failed to send reply.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><FaSpinner className="animate-spin text-4xl text-primary" /></div>;
  if (!ticket) return <div className="text-center py-20">Ticket not found.</div>;

  const displayStatus = ticket.status ? ticket.status.replace('_', ' ') : 'Unknown';
  const statusStyle = ticket.status === 'open' ? 'bg-green-100 text-green-800' : 
                      ticket.status === 'closed' ? 'bg-gray-200 text-gray-700' : 'bg-blue-100 text-blue-800';

  return (
    <div className="flex flex-col h-full min-h-screen bg-gray-50">
      
      {/* HEADER */}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10 shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-3">
                <Link to="/admin/support" className="text-gray-500 hover:text-primary p-2 rounded hover:bg-gray-100">
                    <FaArrowLeft />
                </Link>
                <div>
                    <h1 className="text-lg md:text-xl font-bold text-gray-800 flex flex-wrap items-center gap-2">
                        {ticket.ticket_id}: {ticket.subject}
                        <span className={`text-xs px-2 py-1 rounded-full uppercase ${statusStyle}`}>
                            {displayStatus}
                        </span>
                        {ticket.priority === 'high' && <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full flex items-center gap-1"><FaExclamationCircle /> High Priority</span>}
                    </h1>
                </div>
            </div>
            
            <div className="flex gap-2 w-full md:w-auto">
                {ticket.status !== 'resolved' && (
                    <button onClick={() => handleStatusChange('resolved')} className="flex-1 md:flex-none bg-green-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-green-700 flex items-center justify-center gap-2">
                        <FaCheckCircle /> Resolve
                    </button>
                )}
                {ticket.status === 'resolved' && (
                    <button onClick={() => handleStatusChange('in_progress')} className="flex-1 md:flex-none bg-yellow-500 text-white px-4 py-2 rounded text-sm font-bold hover:bg-yellow-600">
                        Reopen
                    </button>
                )}
            </div>
        </div>
      </div>

      {/* MAIN CONTENT GRID */}
      <div className="flex-grow p-4 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              
              {/* LEFT: CONVERSATION (Takes 2 cols on desktop) */}
              <div className="lg:col-span-2 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-[80vh] lg:h-[calc(100vh-140px)]">
                  
                  {/* Messages Area */}
                  <div className="flex-grow overflow-y-auto p-4 space-y-6 bg-gray-50">
                      {ticket.messages.map((msg) => {
                          const isCustomer = msg.sender_role === 'customer';
                          const isInternal = msg.is_internal_note;
                          const isSystem = msg.sender_role === 'system';

                          return (
                              <div key={msg.id} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                                  <div className={`max-w-[90%] md:max-w-[80%] rounded-lg p-4 shadow-sm border 
                                      ${isInternal ? 'bg-yellow-50 border-yellow-200' : 
                                        isSystem ? 'bg-red-50 border-red-200 w-full max-w-full' :
                                        isCustomer ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-100'}`}>
                                      
                                      <div className="flex items-center gap-2 mb-2 text-xs opacity-75 border-b pb-2 border-black/5">
                                          {isCustomer ? <FaUser /> : <FaUserShield />}
                                          <span className="font-bold">{msg.sender_name}</span>
                                          {isInternal && <span className="bg-yellow-200 text-yellow-800 px-1 rounded font-bold flex items-center gap-1"><FaLock size={10} /> Internal Note</span>}
                                          {isSystem && <span className="bg-red-200 text-red-800 px-1 rounded font-bold">SYSTEM REPORT</span>}
                                          <span className="ml-auto">{new Date(msg.created_at).toLocaleString()}</span>
                                      </div>
                                      
                                      {/* MARKDOWN RENDERING FOR REPORTS */}
                                      <div className="prose prose-sm max-w-none text-gray-800">
                                          <ReactMarkdown>{msg.message}</ReactMarkdown>
                                      </div>

                                      {msg.attachments && msg.attachments.length > 0 && (
                                          <div className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-black/5">
                                              {msg.attachments.map((url, idx) => (
                                                  <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                                      <img src={url} alt="attachment" className="w-20 h-20 object-cover rounded border border-gray-300 hover:opacity-80 transition" />
                                                  </a>
                                              ))}
                                          </div>
                                      )}
                                  </div>
                              </div>
                          );
                      })}
                      <div ref={messagesEndRef} />
                  </div>

                  {/* Reply Box */}
                  <div className={`p-4 border-t border-gray-200 ${isInternalNote ? 'bg-yellow-50' : 'bg-white'}`}>
                      <form onSubmit={handleReply}>
                          <div className="flex items-center justify-between mb-2">
                              <label className="flex items-center cursor-pointer gap-2 text-sm font-bold text-gray-700 select-none">
                                  <div className={`w-10 h-5 rounded-full p-1 transition-colors ${isInternalNote ? 'bg-yellow-500' : 'bg-gray-300'}`}>
                                      <div className={`bg-white w-3 h-3 rounded-full shadow-md transform transition-transform ${isInternalNote ? 'translate-x-5' : ''}`}></div>
                                  </div>
                                  <input type="checkbox" className="hidden" checked={isInternalNote} onChange={() => setIsInternalNote(!isInternalNote)} />
                                  {isInternalNote ? <span className="text-yellow-700 flex items-center gap-1"><FaLock /> Internal Note (Private)</span> : "Public Reply"}
                              </label>
                          </div>

                          {attachments.length > 0 && (
                              <div className="flex gap-2 mb-2 overflow-x-auto">
                                  {attachments.map((url, idx) => (
                                      <img key={idx} src={url} alt="preview" className="w-12 h-12 object-cover rounded border" />
                                  ))}
                              </div>
                          )}
                          
                          <div className="flex gap-2">
                              <label className="cursor-pointer p-3 text-gray-500 hover:bg-gray-100 rounded transition border border-gray-300 bg-white h-fit">
                                  <FaPaperclip />
                                  <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                              </label>
                              
                              <textarea
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  placeholder={isInternalNote ? "Add a private note for the team..." : "Type your reply to the customer..."}
                                  className={`flex-grow p-2 border rounded focus:outline-none resize-none ${isInternalNote ? 'border-yellow-400 focus:ring-2 focus:ring-yellow-400 bg-yellow-50' : 'border-gray-300 focus:ring-2 focus:ring-primary'}`}
                                  rows="2"
                                  disabled={sending}
                              ></textarea>
                              
                              <button 
                                  type="submit" 
                                  disabled={sending || uploading || (!replyText.trim() && attachments.length === 0)}
                                  className={`px-4 md:px-6 rounded font-bold transition disabled:opacity-50 disabled:cursor-not-allowed h-fit py-3 ${isInternalNote ? 'bg-yellow-600 hover:bg-yellow-700 text-white' : 'bg-primary hover:bg-primary-dark text-white'}`}
                              >
                                  {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                              </button>
                          </div>
                      </form>
                  </div>
              </div>

              {/* RIGHT: CONTEXT SIDEBAR (Stacks below on mobile) */}
              <div className="lg:col-span-1 space-y-4">
                  
                  {/* User Info */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Customer</h3>
                      <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold">
                              {ticket.user_name.charAt(0)}
                          </div>
                          <div className="overflow-hidden">
                              <p className="font-bold text-gray-800 truncate">{ticket.user_name}</p>
                              <p className="text-xs text-gray-500 truncate">{ticket.user_email}</p>
                          </div>
                      </div>
                      <Link to={`/admin/users?search=${ticket.user_email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <FaUser /> View Full Profile
                      </Link>
                  </div>

                  {/* Linked Order */}
                  {ticket.related_order_id && (
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                              <FaBox /> Linked Order
                          </h3>
                          <p className="text-sm font-mono bg-gray-100 p-2 rounded mb-2">{ticket.related_order_id}</p>
                          <Link to="/admin/orders" className="block w-full text-center bg-blue-50 text-blue-700 py-2 rounded text-sm font-bold hover:bg-blue-100">
                              Go to Orders
                          </Link>
                      </div>
                  )}

                  {/* Linked Booking */}
                  {ticket.related_booking_id && (
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                              <FaTools /> Linked Service
                          </h3>
                          <p className="text-sm font-mono bg-gray-100 p-2 rounded mb-2">{ticket.related_booking_id}</p>
                          <Link to="/admin/services" className="block w-full text-center bg-purple-50 text-purple-700 py-2 rounded text-sm font-bold hover:bg-purple-100">
                              Go to Bookings
                          </Link>
                      </div>
                  )}

                  {/* Ticket Meta */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Ticket Info</h3>
                      <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                              <span className="text-gray-600">Priority:</span>
                              <span className={`font-bold capitalize ${ticket.priority === 'high' ? 'text-red-600' : 'text-gray-800'}`}>{ticket.priority}</span>
                          </div>
                          <div className="flex justify-between">
                              <span className="text-gray-600">Category:</span>
                              <span className="font-medium">{ticket.category}</span>
                          </div>
                          <div className="flex justify-between">
                              <span className="text-gray-600">Created:</span>
                              <span className="text-xs">{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>
                      </div>
                  </div>

              </div>

          </div>
      </div>
    </div>
  );
}

export default AdminTicketDetail;